/**
 * Leviathan - Import/Export Page
 * Quasar Prime LLC
 *
 * Bulk data import and export tools
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Upload,
  Download,
  Package,
  Users,
  Building2,
  Container,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface ImportJob {
  id: string;
  jobType: string;
  fileName: string;
  status: string;
  totalRows: number;
  successRows: number;
  errorRows: number;
  createdAt: string;
}

const IMPORT_TYPES = [
  { value: 'CUSTOMERS', label: 'Customers', icon: Users },
  { value: 'PRODUCTS', label: 'Products', icon: Package },
  { value: 'TANKS', label: 'Tanks', icon: Container },
  { value: 'SUPPLIERS', label: 'Suppliers', icon: Building2 },
];

const EXPORT_TYPES = [
  { value: 'CUSTOMERS', label: 'Customers' },
  { value: 'PRODUCTS', label: 'Products' },
  { value: 'ORDERS', label: 'Orders' },
  { value: 'INVOICES', label: 'Invoices' },
  { value: 'TANKS', label: 'Tanks' },
  { value: 'SUPPLIERS', label: 'Suppliers' },
];

export default function ImportExport() {
  const { accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<'import' | 'export'>('import');
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);

  // Import state
  const [importType, setImportType] = useState('CUSTOMERS');
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [importOptions, setImportOptions] = useState({
    skipDuplicates: true,
    updateExisting: false,
  });
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // Export state
  const [exportType, setExportType] = useState('CUSTOMERS');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchImportJobs();
  }, [accessToken]);

  const fetchImportJobs = async () => {
    if (!accessToken) return;
    try {
      const response = await fetch(`${API_URL}/api/data/import/jobs`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setImportJobs(data.jobs);
      }
    } catch (error) {
      console.error('Failed to fetch import jobs:', error);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvContent(event.target?.result as string);
      };
      reader.readAsText(file);
    }
  };

  const handleImport = async () => {
    if (!csvContent || !accessToken) return;

    setIsImporting(true);
    setImportResult(null);

    try {
      const response = await fetch(`${API_URL}/api/data/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          type: importType,
          data: csvContent,
          fileName,
          options: importOptions,
        }),
      });

      const data = await response.json();
      setImportResult(data.job);
      fetchImportJobs();
    } catch (error) {
      console.error('Import failed:', error);
      setImportResult({ error: 'Import failed' });
    } finally {
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    if (!accessToken) return;
    setIsExporting(true);

    try {
      const response = await fetch(`${API_URL}/api/data/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          type: exportType,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${exportType.toLowerCase()}_export.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const downloadTemplate = async (type: string) => {
    if (!accessToken) return;
    try {
      const response = await fetch(`${API_URL}/api/data/templates/${type}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type.toLowerCase()}_template.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download template:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Clock className="w-4 h-4 text-amber-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 via-blue-500 to-amber-500" />
        <h1 className="text-3xl font-bold text-slate-100 pl-6">
          Import / Export
        </h1>
        <p className="text-slate-400 text-sm pl-6 mt-1">
          Bulk data import and export tools
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-slate-700">
        <button
          onClick={() => setActiveTab('import')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium uppercase tracking-wider transition-colors ${
            activeTab === 'import'
              ? 'text-amber-500 border-b-2 border-amber-500'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Upload className="w-4 h-4" />
          <span>Import Data</span>
        </button>
        <button
          onClick={() => setActiveTab('export')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium uppercase tracking-wider transition-colors ${
            activeTab === 'export'
              ? 'text-amber-500 border-b-2 border-amber-500'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Download className="w-4 h-4" />
          <span>Export Data</span>
        </button>
      </div>

      {activeTab === 'import' && (
        <div className="grid grid-cols-3 gap-6">
          {/* Import Form */}
          <div className="col-span-2">
            <Panel title="Import Data">
              <div className="space-y-6">
                {/* Type Selection */}
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-wider mb-3">
                    Data Type
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {IMPORT_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.value}
                          onClick={() => setImportType(type.value)}
                          className={`p-3 border text-center transition-colors ${
                            importType === type.value
                              ? 'bg-amber-500/10 border-amber-500 text-amber-500'
                              : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:border-amber-500/50'
                          }`}
                        >
                          <Icon className="w-5 h-5 mx-auto mb-1" />
                          <div className="text-xs">{type.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                    CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="w-full bg-slate-900 border border-slate-700 px-4 py-2 text-slate-100 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-amber-500 file:text-slate-900 file:font-medium file:text-sm file:cursor-pointer"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-slate-500 text-xs">
                      {csvContent
                        ? `${csvContent.split('\n').length - 1} rows detected`
                        : 'No file selected'}
                    </span>
                    <button
                      onClick={() => downloadTemplate(importType)}
                      className="text-amber-500 hover:text-amber-400 text-xs"
                    >
                      Download Template &rarr;
                    </button>
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-3">
                  <label className="block text-slate-400 text-xs uppercase tracking-wider">
                    Options
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importOptions.skipDuplicates}
                      onChange={(e) =>
                        setImportOptions((prev) => ({
                          ...prev,
                          skipDuplicates: e.target.checked,
                        }))
                      }
                      className="rounded border-slate-600 bg-slate-900 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-slate-300 text-sm">
                      Skip duplicate records
                    </span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importOptions.updateExisting}
                      onChange={(e) =>
                        setImportOptions((prev) => ({
                          ...prev,
                          updateExisting: e.target.checked,
                        }))
                      }
                      className="rounded border-slate-600 bg-slate-900 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-slate-300 text-sm">
                      Update existing records
                    </span>
                  </label>
                </div>

                <button
                  onClick={handleImport}
                  disabled={!csvContent || isImporting}
                  className="w-full flex items-center justify-center space-x-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium py-3 px-4 uppercase tracking-wider text-sm disabled:opacity-50"
                >
                  {isImporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  <span>{isImporting ? 'Importing...' : 'Start Import'}</span>
                </button>

                {/* Import Result */}
                {importResult && (
                  <div
                    className={`p-4 border ${
                      importResult.error || importResult.errorRows > 0
                        ? 'bg-red-500/10 border-red-500/50'
                        : 'bg-green-500/10 border-green-500/50'
                    }`}
                  >
                    {importResult.error ? (
                      <div className="text-red-400">{importResult.error}</div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-slate-100 font-medium">
                          Import{' '}
                          {importResult.status === 'COMPLETED'
                            ? 'completed'
                            : 'in progress'}
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-slate-400">Total: </span>
                            <span className="text-slate-100">
                              {importResult.totalRows}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-400">Success: </span>
                            <span className="text-green-400">
                              {importResult.successRows}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-400">Errors: </span>
                            <span className="text-red-400">
                              {importResult.errorRows}
                            </span>
                          </div>
                        </div>
                        {importResult.errors?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-700">
                            <div className="text-red-400 text-xs mb-2">
                              Errors:
                            </div>
                            <div className="max-h-32 overflow-y-auto space-y-1">
                              {importResult.errors
                                .slice(0, 10)
                                .map((err: any, i: number) => (
                                  <div
                                    key={i}
                                    className="text-xs text-slate-400"
                                  >
                                    Row {err.row}: {err.message}
                                  </div>
                                ))}
                              {importResult.errors.length > 10 && (
                                <div className="text-xs text-slate-500">
                                  ...and {importResult.errors.length - 10} more
                                  errors
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Panel>
          </div>

          {/* Import History */}
          <div>
            <Panel title="Recent Imports">
              {importJobs.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No import history
                </div>
              ) : (
                <div className="space-y-3">
                  {importJobs.map((job) => (
                    <div
                      key={job.id}
                      className="p-3 bg-slate-900/50 border border-slate-700"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-amber-500 text-sm font-medium">
                          {job.jobType}
                        </span>
                        <div className="flex items-center space-x-1">
                          {getStatusIcon(job.status)}
                          <span className="text-xs text-slate-400">
                            {job.status}
                          </span>
                        </div>
                      </div>
                      <div className="text-slate-500 text-xs">
                        {formatDate(job.createdAt)}
                      </div>
                      <div className="text-slate-400 text-xs mt-1">
                        {job.successRows}/{job.totalRows} rows &bull;{' '}
                        {job.errorRows} errors
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </div>
      )}

      {activeTab === 'export' && (
        <div className="max-w-xl">
          <Panel title="Export Data">
            <div className="space-y-6">
              <div>
                <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                  Data Type
                </label>
                <select
                  value={exportType}
                  onChange={(e) => setExportType(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 px-4 py-2 text-slate-100"
                >
                  {EXPORT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleExport}
                disabled={isExporting}
                className="w-full flex items-center justify-center space-x-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium py-3 px-4 uppercase tracking-wider text-sm disabled:opacity-50"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>{isExporting ? 'Exporting...' : 'Download CSV'}</span>
              </button>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}

function Panel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber-500" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber-500" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber-500" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber-500" />

      <div className="bg-slate-800 border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-slate-100 uppercase tracking-wide mb-4 pb-3 border-b border-amber-500/50">
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
